import styled from "@emotion/styled";
import { yupResolver } from "@hookform/resolvers/yup";
import { Close, Delete } from "@mui/icons-material";
import {
  Alert,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  Typography
} from "@mui/material";
import { Button } from "components/UI";
import { DateField } from "components/UI/InputFields/DateField";
import { MutationError } from "components/UI/MutationError";
import { useChurchStore } from "features/churches";
import moment from "moment";
import { useCallback, useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import { useCreateMemberJournal, useUpdateMemberJournal } from "../api";

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`;

const Label = styled.label`
  color: #5b5b5b;
  font-size: 12px;
  font-weight: 500;
`;

const Input = styled.input`
  border-radius: 4px;
  border: 0.73px solid #b3b3b3;
  height: 40px;
  outline: none;
  padding: 8px;
`;

const Textarea = styled.textarea`
  resize: none;
  border-radius: 4px;
  border: 0.73px solid #b3b3b3;
  outline: none;
  padding: 8px;
  font-size: 12px;
  font-family: inherit;
`;

const ErrorText = styled.span`
  color: #d32f2f;
  font-size: 10px;
`;

const Filename = styled.span`
  font-size: 10px;
`;

const DropzoneContainer = styled.div`
  background-color: rgba(0, 0, 0, 0.05);
  border: 1px dashed #b3b3b3;
  border-radius: 4px;
  padding: 16px;
  text-align: center;
  cursor: pointer;

  &:hover {
    border-color: #3dadb7;
  }
`;

const DropzoneText = styled.p`
  margin: 0;
  font-size: 14px;
  color: #5b5b5b;
`;

const StyledSelect = styled(Select)`
  & .MuiOutlinedInput-notchedOutline {
    border: 0.73px solid #b3b3b3;
    border-radius: 4px;
  }

  & .MuiSelect-select {
    padding: 8px;
  }

  &.MuiOutlinedInput-root {
    font-size: 14px;
  }

  &.Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: #3dadb7;
    border-width: 0.73px;
  }
`;

const StyledDateField = styled(DateField)`
  & .MuiOutlinedInput-root {
    border-radius: 4px;
    border: 0.73px solid #b3b3b3;
    height: 40px;
    font-size: 14px;
    background-color: transparent;
  }

  & .MuiOutlinedInput-notchedOutline {
    border: none;
  }

  & .MuiInputBase-input {
    padding: 8px;
  }

  &.Mui-focused .MuiOutlinedInput-root {
    border-color: #3dadb7;
  }
`;

const HelperText = styled.span`
  font-size: 12px;
`;

const schema = yup.object().shape({
  member_id: yup.number().required("Member ID is required"),
  event_date: yup.string().required("Event date is required"),
  record_type: yup
    .string()
    .oneOf(
      ["LIFE_EVENT", "CHURCH_ENGAGEMENT", "CHURCH_SERVICE"],
      "Invalid record type"
    )
    .required("Record type is required"),
  event_name: yup.array().of(yup.string()).required("Event name is required"),
  description: yup.string().optional(),
  note: yup.string().optional(),
  actionee: yup.string().optional()
});

export function JournalModal({ open, onClose, memberId, initialData }) {
  const [attachments, setAttachments] = useState([]);
  const [existingAttachments, setExistingAttachments] = useState([]);
  const selectedChurch = useChurchStore(state => state.selectedChurch);

  const createMemberJournalMutation = useCreateMemberJournal();
  const updateMemberJournalMutation = useUpdateMemberJournal();

  const navigate = useNavigate();

  const onDrop = useCallback(acceptedFiles => {
    setAttachments(prev => [...prev, ...acceptedFiles]);
  }, []);

  const { getRootProps, getInputProps, fileRejections } = useDropzone({
    onDrop,
    accept: {
      "image/png": [".png"],
      "image/jpeg": [".jpg", ".jpeg"],
      "application/pdf": [".pdf"]
    },
    maxSize: 2 * 1024 * 1024, // 2 MB
    multiple: true
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
    setValue,
    watch
  } = useForm({
    defaultValues: {
      member_id: memberId,
      event_date: "",
      record_type: "LIFE_EVENT",
      event_name: [],
      description: "",
      note: "",
      actionee: ""
    },
    resolver: yupResolver(schema)
  });

  useEffect(() => {
    if (initialData) {
      setValue("event_date", moment(initialData.event_date).toDate());
      setValue("record_type", initialData.record_type || "LIFE_EVENT");
      setValue("event_name", initialData.event_name || []);
      setValue("description", initialData.description || "");
      setValue("note", initialData.note || "");
      setValue("actionee", initialData.actionee || "");
      setExistingAttachments(initialData.attachments || []);
    } else {
      reset();
      setAttachments([]);
      setExistingAttachments([]);
    }
  }, [initialData, setValue, reset]);

  const onSubmit = data => {
    const formData = new FormData();

    // Append form fields
    Object.keys(data).forEach(key => {
      if (key === "event_name") {
        data[key].forEach((item, index) => {
          formData.append(`event_name[${index}]`, item);
        });
      } else if (key === "event_date") {
        formData.append(key, moment(data[key]).format("YYYY-MM-DDTHH:mm"));
      } else {
        formData.append(key, data[key]);
      }
    });

    const newAndExistingAttachments = [...attachments, ...existingAttachments];

    if (newAndExistingAttachments.length > 0) {
      newAndExistingAttachments.forEach((file, index) => {
        formData.append(`attachments[${index}]`, file);
      });
    }

    if (initialData) {
      updateMemberJournalMutation.mutate(
        {
          churchId: selectedChurch?.id,
          memberId: memberId,
          journalId: initialData.id,
          memberJournalData: formData
        },
        {
          onSuccess: () => {
            reset();
            setAttachments([]);
            setExistingAttachments([]);
            onClose();
          }
        }
      );
    } else {
      createMemberJournalMutation.mutate(
        {
          churchId: selectedChurch?.id,
          memberJournalData: formData
        },
        {
          onSuccess: () => {
            reset();
            setAttachments([]);
            setExistingAttachments([]);
            onClose();
          }
        }
      );
    }
  };

  const removeAttachment = index => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const removeExistingAttachment = index => {
    setExistingAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const recordType = watch("record_type");
  const eventName = watch("event_name");

  return (
    <Dialog fullWidth maxWidth="xs" open={open} onClose={onClose}>
      <DialogTitle sx={{ bgcolor: "primary.main" }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="subtitle1" color="primary.contrastText">
            {initialData ? "Edit member journal" : "Add member journal"}
          </Typography>
          <IconButton onClick={onClose}>
            <Close sx={{ color: "primary.contrastText" }} />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        <Stack p={2} spacing={2}>
          <Field>
            <Label htmlFor="event_date">Date and Time</Label>
            <Controller
              name="event_date"
              control={control}
              render={({ field }) => (
                <StyledDateField
                  id="event_date"
                  value={field.value}
                  onChange={field.onChange}
                  inputRef={field.ref}
                />
              )}
            />
            {errors.event_date && (
              <ErrorText>{errors.event_date.message}</ErrorText>
            )}
          </Field>
          <Field>
            <Label htmlFor="record_type">Record Type</Label>
            <Controller
              name="record_type"
              control={control}
              defaultValue="LIFE_EVENT"
              render={({ field }) => (
                <StyledSelect {...field} input={<OutlinedInput />} fullWidth>
                  <MenuItem value="LIFE_EVENT">Life Event</MenuItem>
                  <MenuItem value="CHURCH_ENGAGEMENT">
                    Church Engagement
                  </MenuItem>
                  <MenuItem value="CHURCH_SERVICE">Church Service</MenuItem>
                </StyledSelect>
              )}
            />
            {errors.record_type && (
              <ErrorText>{errors.record_type.message}</ErrorText>
            )}
          </Field>
          <Field>
            <Label htmlFor="event_name">Event Name</Label>
            <Controller
              name="event_name"
              control={control}
              defaultValue={[]}
              render={({ field }) => (
                <StyledSelect
                  {...field}
                  multiple
                  input={<OutlinedInput />}
                  fullWidth
                  renderValue={selected => selected.join(", ")}
                  MenuProps={{
                    anchorOrigin: {
                      vertical: "top",
                      horizontal: "center"
                    },
                    transformOrigin: {
                      vertical: "bottom",
                      horizontal: "center"
                    }
                  }}
                >
                  {recordType === "LIFE_EVENT" && [
                    <MenuItem key="birth" value="Birth">
                      Birth
                    </MenuItem>,
                    <MenuItem key="salvation" value="Salvation">
                      Salvation
                    </MenuItem>,
                    <MenuItem key="baptism" value="Baptism">
                      Baptism
                    </MenuItem>,
                    <MenuItem key="graduated" value="Graduated">
                      Graduated
                    </MenuItem>,
                    <MenuItem key="get-job" value="Get job">
                      Get job
                    </MenuItem>,
                    <MenuItem key="retired" value="Retired">
                      Retired
                    </MenuItem>,
                    <MenuItem key="get-married" value="Get married">
                      Get married
                    </MenuItem>,
                    <MenuItem key="got-child" value="Got child">
                      Got child
                    </MenuItem>,
                    <MenuItem key="dedicate-child" value="Dedicate child">
                      Dedicate child
                    </MenuItem>,
                    <MenuItem key="divorce" value="Divorce">
                      Divorce
                    </MenuItem>,
                    <MenuItem
                      key="losing-spouse"
                      value="Losing a spouse via death"
                    >
                      Losing a spouse via death
                    </MenuItem>,
                    <MenuItem key="resign" value="Resign from church">
                      Resign from church
                    </MenuItem>,
                    <MenuItem key="death" value="Death">
                      Death
                    </MenuItem>
                  ]}
                  {recordType === "CHURCH_ENGAGEMENT" && [
                    <MenuItem
                      key="join-church"
                      value="Join church / Membership date"
                    >
                      Join church / Membership date
                    </MenuItem>,
                    <MenuItem key="join-cell-group" value="Join cell group">
                      Join cell group
                    </MenuItem>,
                    <MenuItem
                      key="join-ministry-team"
                      value="Join ministry team"
                    >
                      Join ministry team
                    </MenuItem>,
                    <MenuItem
                      key="join-leadership-team"
                      value="Join leadership team"
                    >
                      Join leadership team
                    </MenuItem>,
                    <MenuItem
                      key="resign-from-ministry-team"
                      value="Resign from the ministry team"
                    >
                      Resign from the ministry team
                    </MenuItem>,
                    <MenuItem
                      key="resign-from-leadership-team"
                      value="Resign from the leadership team"
                    >
                      Resign from the leadership team
                    </MenuItem>,
                    <MenuItem key="took-training" value="Took training">
                      Took training
                    </MenuItem>,
                    <MenuItem key="attend-event" value="Attend event">
                      Attend event
                    </MenuItem>
                  ]}
                  {recordType === "CHURCH_SERVICE" && [
                    <MenuItem key="home-visits" value="Home Visits">
                      Home Visits
                    </MenuItem>,
                    <MenuItem
                      key="office-visits"
                      value="Office/Workplace Visits"
                    >
                      Office/Workplace Visits
                    </MenuItem>,
                    <MenuItem key="hospital-visits" value="Hospital Visits">
                      Hospital Visits
                    </MenuItem>,
                    <MenuItem key="counseling" value="One-on-One Counseling">
                      One-on-One Counseling
                    </MenuItem>,
                    <MenuItem key="prayer" value="Prayer">
                      Prayer
                    </MenuItem>,
                    <MenuItem key="group-sessions" value="Group Sessions">
                      Group Sessions
                    </MenuItem>,
                    <MenuItem key="teachings" value="Teachings given">
                      Teachings given
                    </MenuItem>,
                    <MenuItem key="training" value="Training given">
                      Training given
                    </MenuItem>,
                    <MenuItem key="coaching" value="Coaching given">
                      Coaching given
                    </MenuItem>
                  ]}
                </StyledSelect>
              )}
            />
            {errors.event_name && (
              <ErrorText>{errors.event_name.message}</ErrorText>
            )}
          </Field>
          {eventName?.includes("Get married") && (
            <HelperText>
              Please make sure to change your marital status in your profile
              before creating a journal for this event.
              <Button
                flavor="white"
                sx={{ p: 0, textTransform: "none", fontSize: "10px" }}
                onClick={() => {
                  navigate(`/members/${memberId}/update`);
                }}
              >
                Change marital status here
              </Button>
            </HelperText>
          )}
          <Field>
            <Label htmlFor="description">Description</Label>
            <Textarea id="description" rows={4} {...register("description")} />
            {errors.description && (
              <ErrorText>{errors.description.message}</ErrorText>
            )}
          </Field>
          <Field>
            <Label>Attachments</Label>
            {existingAttachments.length > 0 && (
              <Stack spacing={1} sx={{ mb: 1 }}>
                {existingAttachments.map((filename, index) => (
                  <Chip
                    key={index}
                    label={filename}
                    onDelete={() => removeExistingAttachment(index)}
                  />
                ))}
              </Stack>
            )}
            <DropzoneContainer {...getRootProps()}>
              <input {...getInputProps()} />
              <DropzoneText>
                Drop files (photo, pdf) here or click to upload
              </DropzoneText>
            </DropzoneContainer>
            {attachments.length > 0 && (
              <Stack spacing={1} sx={{ mt: 1 }}>
                {attachments.map((file, index) => (
                  <Stack
                    key={index}
                    direction="row"
                    alignItems="center"
                    spacing={1}
                  >
                    <Filename>{file.name}</Filename>
                    <IconButton
                      size="small"
                      onClick={() => removeAttachment(index)}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Stack>
                ))}
              </Stack>
            )}

            {fileRejections.length > 0 && (
              <Alert severity="error">
                {fileRejections
                  .map(({ errors }) =>
                    errors.map(error => {
                      if (error.code === "file-too-large") {
                        return "File size is too large. Maximum size is 2 MB.";
                      }
                      if (error.code === "file-invalid-type") {
                        return "Invalid file type. Please upload PNG, JPG or PDF files only.";
                      }
                      return error.message;
                    })
                  )
                  .join(", ")}
              </Alert>
            )}
          </Field>
          <Field>
            <Label htmlFor="note">Notes</Label>
            <Input id="note" {...register("note")} />
            {errors.note && <ErrorText>{errors.note.message}</ErrorText>}
          </Field>
          <Field>
            <Label htmlFor="actionee">Actionee</Label>
            <Input id="actionee" {...register("actionee")} />
            {errors.actionee && (
              <ErrorText>{errors.actionee.message}</ErrorText>
            )}
          </Field>

          <MutationError
            error={
              createMemberJournalMutation.error ||
              updateMemberJournalMutation.error
            }
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          flavor="text"
          onClick={onClose}
          disabled={
            createMemberJournalMutation.isLoading ||
            updateMemberJournalMutation.isLoading
          }
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          disabled={
            createMemberJournalMutation.isLoading ||
            updateMemberJournalMutation.isLoading
          }
        >
          {initialData ? "Save changes" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
